import { HeadingTag } from '@/globals/HeadingTag';
import {
  Component,
  Element,
  h,
  Host,
  Prop,
  State,
} from '@stencil/core';

/** A short description about `pn-versa-card` */
@Component({
  tag: 'pn-versa-card',
  styleUrl: 'pn-versa-card.scss',
})
export class PnVersaCard {

  @Element() hostElement: HTMLElement;

  @Prop() label?: string;
  @Prop() heading?: string;
  @Prop() preamble?: string;
  @Prop() imageGrow = false;

  // Layout / modifiers
  @Prop() horizontal = false;
  @Prop() imgAlignment?: 'left' | 'right' = 'left';
  @Prop() hasImageCover = false;
  @Prop() alignMiddle = false;
  @Prop() center = false;

  @Prop() roundImage = false;
  @Prop() headingLevel: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h3';

  @Prop() opacity: number = 90;
  @Prop() transparentBackground = false;

  private cx(parts: Array<string | false | null | undefined>) {
    return parts.filter(Boolean).join(' ');
  }

  private get hasImageCoverText(): boolean {
    return !!(this.label || this.heading || this.preamble || this.showRte || this.showCta);
  }

  private get normalizedOpacity(): number {
    return Math.min(100, Math.max(0, this.opacity ?? 90));
  }

  // This getter reads the data-card-color attribute, which is used to set CSS variables for background and text colors in the SCSS file. It returns the color value or null if not set.
  private get cardColor() {
    const value = this.hostElement.getAttribute('data-card-color')?.trim().toLowerCase();
    return value || null;
  }

  private get hasBg(): boolean {
    return !!this.cardColor;
  }

  private get cardClass() {

    return this.cx([
      'pn-versa-card',

      // modifiers      
      this.horizontal && 'pn-versa-card-horizontal',
      this.horizontal && this.imgAlignment === 'right' && 'pn-versa-card-horizontal--imgRight',
      this.hasBg && 'pn-versa-card-hasBg',
      this.hasImageCover && 'pn-versa-card-hasImageCover', // can be true without text, just means image covers whole card without text on top
      this.hasImageCover && this.hasImageCoverText && 'pn-versa-card-hasImageCoverText', //has to have imageCover true AND imageCoverText in order for class to be set
      this.roundImage && 'pn-versa-card-hasRoundedImage',
      this.center && 'pn-versa-card-center',
      this.alignMiddle && 'pn-versa-card-alignMiddle',
      this.imageGrow && 'pn-versa-card-imageGrow',
      this.hasBg && this.transparentBackground && 'pn-versa-card-transparentBackground',

    ]);
  }

  private get showContent(): boolean {
    return this.showTextContent || this.showCta;
  }

  private get showTextContent(): boolean {
    return !!(this.label || this.heading || this.preamble || this.showRte);
  }

  @State() showImage: boolean = false;
  @State() showRte: boolean = false;
  @State() showCta: boolean = false;

  componentWillLoad() {
    this.showImage = !!this.hostElement.querySelector('[slot="image"]');
    this.showRte = !!this.hostElement.querySelector('[slot="rte"]');
    this.showCta = !!this.hostElement.querySelector('[slot="cta"]');

  }

  render() {
    return (
      <Host>
        <div class={this.cardClass} style={{ '--pn-versa-opacity': `${this.normalizedOpacity / 100}` }}>
          {this.showImage && (
            <div class="pn-versa-card-imageWrapper">
              <slot name="image" />
            </div>)}
          {this.showContent && (
            <div class="pn-versa-card-content">
              {this.showTextContent && <div class="pn-versa-card-textContent">
                {this.label && (
                  <span class="pn-versa-card-label">{this.label}</span>
                )}

                {this.heading && (
                  <HeadingTag cssClass="pn-versa-card-heading" level={this.headingLevel}>{this.heading}</HeadingTag>
                )}

                {this.preamble && (
                  <p class="pn-versa-card-preamble">{this.preamble}</p>
                )}

                {this.showRte && <div class="pn-versa-card-RTEtext">
                  <slot name="rte" />
                </div>
                }
              </div>}

              {this.showCta && <div class="pn-versa-card-linkwrapper">
                <slot name="cta" />
              </div>}

            </div>
          )}
        </div>
      </Host>
    );
  }
}